/**
 * TanStack Query hooks against the backend REST API.
 * Polling cadences match the architecture doc:
 *   /me                  ─ no polling (identity is JWT-stable)
 *   /devices             ─ 30 s
 *   /devices/:id         ─ 5 s   (device detail page wants near-live)
 *   /devices/:id/events  ─ 15 s
 *   /analytics/*         ─ 60 s
 *
 * `refetchInterval` is set to false when the tab is hidden — that's the
 * TanStack Query default; we don't need to opt in.
 */

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { api } from './client';
import type { ExportCounts, ExportRecord, ExportSelection } from '../lib/historyExport';

export interface MeResponse {
  sub: string;
  email: string | null;
  tenant_id: string;
  role: 'super-admin' | 'tenant-admin' | 'tenant-viewer';
  scope: 'all' | 'own';
  groups: string[];
}

export interface GeoFix {
  // DE-162: current verified position can be null when the most recent
  // resolution failed (low-confidence / no-fix / error). The portal
  // should fall back to last_verified_* for a faded "last known" pin in
  // that case rather than displaying nothing or a stale current pin.
  lat: number | null;
  lon: number | null;
  accuracy: number | null;
  source: string | null;
  timestamp: string | number | null;
  // Last verified position — persists across failure runs so we can
  // still pin the map (faded) while the current position is unverified.
  last_verified_lat?: number | null;
  last_verified_lon?: number | null;
  last_verified_accuracy?: number | null;
  last_verified_source?: string | null;
  last_verified_at?: number | null;
  // DE-170 — Geoapify-resolved address and best-guess company/school for the
  // current position, mirrored under last_verified_* like the coords above.
  // Null when enrichment is unconfigured/unavailable, or when the fix was too
  // coarse for place attribution (place_name only). `address` degrades to
  // "City, Country" instead of a street on coarse/cell fixes.
  address?: string | null;
  place_name?: string | null;
  // Kind of place ("conference_centre") for a fix whose venue has no name of
  // its own in the open data. The shadow never carries this — the backend
  // merges it in from the newest enriched geo_history row — so unlike the two
  // fields above there is no last_verified_* mirror to fall back to.
  place_type?: string | null;
  last_verified_address?: string | null;
  last_verified_place_name?: string | null;
  // Per-attempt metadata, updated on every Lambda invocation. Lets the
  // portal say "Verified X ago — N inconclusive scans since".
  last_scan_request: string | number | null;
  last_attempt_status?:
    | 'good'
    | 'stationary'
    | 'low-confidence'
    | 'no-fix'
    | 'error'
    | 'cell-fallback' // DE-203: WiFi fix rejected (roaming-AP hijack); reporting cell
    | null;
  last_attempt_accuracy?: number | null;
  last_attempt_at?: number | null;
  consecutive_failures?: number;
  // DE-192 — epoch-seconds of the last attempt that actually reached Google.
  // An attempt whose last_google_at is older than itself was answered from the
  // WiFi-fingerprint cache: still a re-affirmation, but weaker evidence than an
  // independent resolve, and the operator is entitled to know which they have.
  last_google_at?: number | null;
  // DE-281 — verdict of the DE-203 cell-vs-WiFi cross-check for the resolve
  // that produced this fix. Before DE-281 this lived only in CloudWatch, so a
  // WiFi-only fix and a cell-corroborated one looked identical in the Portal.
  // Absent on fixes written before the resolver started persisting it.
  cell_check?:
    | 'agree'
    | 'no-cell-fix'
    | 'not-attempted'
    | 'wifi-rejected'
    | 'wifi-rejected-unusable'
    | null;
  cell_check_at?: number | null;
}

/**
 * Resolve a GeoFix into the best available coordinates plus a flag
 * indicating whether the position is currently verified. Returns null
 * when neither the current nor the last_verified_* fields carry usable
 * coords. Callers use the `verified` flag to render the pin/circle
 * faded vs solid.
 */
export interface EffectiveFix {
  lat: number;
  lon: number;
  accuracy: number;
  source: string | null;
  timestamp: number | string | null;
  verified: boolean;
  // DE-170 — taken from whichever branch supplied the coords, so callers
  // never have to know whether they're looking at the current or the
  // last-verified position.
  address: string | null;
  place_name: string | null;
  place_type: string | null;
}
export function effectiveFix(geo: GeoFix | null | undefined): EffectiveFix | null {
  if (!geo) return null;
  const isNum = (v: unknown): v is number =>
    typeof v === 'number' && Number.isFinite(v);
  if (isNum(geo.lat) && isNum(geo.lon)) {
    return {
      lat: geo.lat,
      lon: geo.lon,
      accuracy: isNum(geo.accuracy) ? geo.accuracy : 0,
      source: geo.source,
      timestamp: geo.timestamp,
      verified: true,
      address: geo.address ?? null,
      place_name: geo.place_name ?? null,
      place_type: geo.place_type ?? null,
    };
  }
  if (isNum(geo.last_verified_lat) && isNum(geo.last_verified_lon)) {
    return {
      lat: geo.last_verified_lat,
      lon: geo.last_verified_lon,
      accuracy: isNum(geo.last_verified_accuracy) ? geo.last_verified_accuracy : 0,
      source: geo.last_verified_source ?? null,
      timestamp: geo.last_verified_at ?? null,
      verified: false,
      address: geo.last_verified_address ?? null,
      place_name: geo.last_verified_place_name ?? null,
      // No last_verified mirror exists; the merged value describes the current
      // position, so it travels with either branch.
      place_type: geo.place_type ?? null,
    };
  }
  return null;
}

export interface DeviceSummary {
  thing: string;
  tenant_id: string;
  role: string;
  last_seen: string | null;
  created_at: string;
  fw_ver: string | null;
  // DE-128 — uC-reported serial from shadow.device.serial; null if uC hasn't published yet.
  serial: string | null;
  mender_artifact: string | null;
  /**
   * DE-310 — which board, from the DE-306 generated column over the shadow.
   * NULL until DE-308's telemetry reaches the unit, which is most of the fleet
   * today. Resolve it through lib/hardware.ts rather than reading it raw: an
   * absence must render as an assumption, never as a fact.
   */
  device_type: string | null;
  // DE-223 — public release ID the device reports (shadow `release`). null when
  // it has never received a published release, which is the normal case today.
  release_id: string | null;
  release_source: string | null;
  // DE-318 — which release a pending device is waiting on. Optional: a backend
  // older than this field does not send it, and the dashboard then shows the
  // plain "staged" it always did.
  release_pending_id?: string | null;
  sys_state: number | null;
  geo: GeoFix | null;
  // DE-134 — MQTT lifecycle-event-driven presence. `online` is the
  // backend-computed truth (lifecycle + 15-min last_seen freshness).
  online: boolean;
  last_connected_at: string | null;
  last_disconnected_at: string | null;
  last_disconnect_reason: string | null;
  // Operator-set human label. NULL until set via PUT
  // /admin/devices/:id/friendly-name (tenant-admin + super-admin).
  friendly_name: string | null;
}

export interface DeviceDetail {
  device: {
    thing: string;
    tenant_id: string;
    role: string;
    last_seen: string | null;
    created_at: string;
    shadow_doc: Record<string, unknown>;
    geo: GeoFix | null;
    // DE-134 — see DeviceSummary.
    online: boolean;
    last_connected_at: string | null;
    last_disconnected_at: string | null;
    last_disconnect_reason: string | null;
    friendly_name: string | null;
  };
  latestTelemetry: null | {
    ts: string;
    cpu_percent: number | null;
    cpu_temp_c: number | null;
    mem_used_mb: number | null;
    disk_data_used_gb: number | null;
    modem_signal: number | null;
    throttled_now: boolean | null;
    load_1min: number | null;
    uptime_s: number | null;
  };
}

export interface EventRow {
  event_id: string;
  ts: string;
  event_type: string;
  session_id: string | null;
  data: Record<string, unknown>;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => api<MeResponse>('/me'),
    staleTime: Infinity,
  });
}

/**
 * DE-306 — the board registry. `staleTime: Infinity`, the same shape as
 * `useMe`: hardware models do not change while a tab is open, and every
 * device-type selector in the Portal reads this rather than a literal.
 */
export interface HardwareType {
  id: string;             // canonical Mender device type, e.g. artomeui-r110x
  family: string;         // boards sharing a build tree
  display_name: string;   // what a human reads, e.g. R1125-10
  sku_prefixes: string[];
  mender_aliases: string[];
  active: boolean;
}

export function useHardwareTypes() {
  return useQuery({
    queryKey: ['hardware-types'],
    queryFn: () => api<{ hardwareTypes: HardwareType[] }>('/hardware-types'),
    staleTime: Infinity,
  });
}

export function useDevices() {
  return useQuery({
    queryKey: ['devices'],
    queryFn: () => api<{ devices: DeviceSummary[] }>('/devices'),
    refetchInterval: 30_000,
  });
}

export function useDevice(thing: string | undefined) {
  return useQuery({
    queryKey: ['device', thing],
    queryFn: () => api<DeviceDetail>(`/devices/${encodeURIComponent(thing!)}`),
    enabled: Boolean(thing),
    refetchInterval: 5_000,
  });
}

export function useDeviceEvents(thing: string | undefined, limit = 50) {
  return useQuery({
    queryKey: ['device-events', thing, limit],
    queryFn: () =>
      api<{ events: EventRow[]; nextBefore: string | null }>(
        `/devices/${encodeURIComponent(thing!)}/events?limit=${limit}`,
      ),
    enabled: Boolean(thing),
    refetchInterval: 15_000,
  });
}

// ─── History export ───────────────────────────────────────────────────
// Row counts for the export dialog: type picker, progress total, manifest.
export function useHistoryExportSummary(thing: string | undefined, since: string, until: string) {
  return useQuery({
    queryKey: ['history-export-summary', thing, since, until],
    queryFn: () =>
      api<{ since: string; until: string; counts: ExportCounts }>(
        `/devices/${encodeURIComponent(thing!)}/history-export/summary?${new URLSearchParams({ since, until })}`,
      ),
    enabled: Boolean(thing),
  });
}

/**
 * Pages GET /devices/:id/history-export until nextCursor is null. Throws on
 * any failed page (and on abort) — the caller must never save a partial file.
 */
export async function downloadHistoryExport(
  thing: string,
  sel: ExportSelection,
  onProgress: (fetched: number) => void,
  signal: AbortSignal,
): Promise<ExportRecord[]> {
  const records: ExportRecord[] = [];
  const base: Record<string, string> = {
    since: sel.since,
    until: sel.until,
    sources: sel.sources.join(','),
  };
  if (sel.types) base.types = sel.types.join(',');
  let cursor: string | null = null;
  do {
    const qs = new URLSearchParams(cursor ? { ...base, cursor } : base);
    const page: { records: ExportRecord[]; nextCursor: string | null } = await api(
      `/devices/${encodeURIComponent(thing)}/history-export?${qs}`,
      { signal },
    );
    records.push(...page.records);
    onProgress(records.length);
    cursor = page.nextCursor;
  } while (cursor);
  return records;
}

// ─── Location history ─────────────────────────────────────────────────

/**
 * One nearby company/school/shop from the Geoapify Places lookup, nearest
 * first. A fix accurate to ~100 m can legitimately sit inside several
 * businesses, so the UI shows the best guess plus these alternatives rather
 * than one false certainty (DE-170).
 */
export interface PlaceCandidate {
  name: string;
  type: string | null;        // 'office' | 'education' | 'commercial'
  distance_m: number | null;
}

export interface GeoHistoryPoint {
  ts: string;            // ISO timestamp
  lat: number;
  lon: number;
  accuracy_m: number;
  source: string | null; // 'wifi', 'cell', 'wifi+cell', null
  // DE-170 — all null for rows recorded before enrichment shipped, or when
  // Geoapify was unavailable. `address` is street-level only on tight
  // non-cell fixes; coarser ones carry "City, Country". place_* require a
  // fix within 500 m.
  address: string | null;
  place_name: string | null;
  place_type: string | null;
  place_candidates: PlaceCandidate[] | null;
}

export interface GeoHistoryResponse {
  since: string;
  until: string;
  points: GeoHistoryPoint[];  // newest first per backend ORDER BY
}

export function useDeviceLocationHistory(
  thing: string | undefined,
  sinceISO: string | undefined,
  untilISO?: string,
) {
  return useQuery({
    queryKey: ['device-location-history', thing, sinceISO, untilISO ?? null],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (sinceISO) qs.set('since', sinceISO);
      if (untilISO) qs.set('until', untilISO);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api<GeoHistoryResponse>(
        `/devices/${encodeURIComponent(thing!)}/location-history${suffix}`,
      );
    },
    enabled: Boolean(thing) && Boolean(sinceISO),
    refetchInterval: 60_000,
  });
}

// ─── Session drill-down ───────────────────────────────────────────────

export interface DemoSessionRow {
  event_id: string;
  ts: string;            // session END (when demo_session event fired)
  session_id: string;    // ULID — start time encoded in first 10 chars
  data: {
    active_s?: number;
    suspended_s?: number;
    view_count?: number;
    content_play_count?: number;
    reached_feedback?: boolean;
  };
}

export function useDeviceSessions(thing: string | undefined, day: string | undefined) {
  return useQuery({
    queryKey: ['device-sessions', thing, day],
    queryFn: () =>
      api<{ day: string; sessions: DemoSessionRow[] }>(
        `/devices/${encodeURIComponent(thing!)}/sessions?day=${encodeURIComponent(day!)}`,
      ),
    enabled: Boolean(thing) && Boolean(day),
  });
}

export interface SessionTimelineResponse {
  session: DemoSessionRow | null;
  start: string;
  end: string;
  events: EventRow[];   // already sorted ts ASC
}

export function useDeviceSessionTimeline(
  thing: string | undefined,
  sessionId: string | undefined,
) {
  return useQuery({
    queryKey: ['device-session-timeline', thing, sessionId],
    queryFn: () =>
      api<SessionTimelineResponse>(
        `/devices/${encodeURIComponent(thing!)}/sessions/${encodeURIComponent(sessionId!)}`,
      ),
    enabled: Boolean(thing) && Boolean(sessionId),
  });
}

// ─── State history (DE-137 swim-lane timeline) ────────────────────────────

export interface StateHistoryEvent {
  event_id: string;
  ts: string;
  event_type: string;
  data: Record<string, unknown>;
}

export interface StateHistoryResponse {
  since: string;
  until: string;
  events: StateHistoryEvent[];
  // DISTINCT ON (event_type) latest row strictly before `since` for each
  // axis-bearing type (state_change, demo_mode_change, source_dwell,
  // presence). The SPA merges these with events so the leading edge of
  // a narrowed window paints with the value the device was actually in,
  // not "unknown".
  anchors: StateHistoryEvent[];
}

export function useDeviceStateHistory(
  thing: string | undefined,
  sinceISO: string | undefined,
  untilISO?: string,
) {
  return useQuery({
    queryKey: ['device-state-history', thing, sinceISO, untilISO ?? null],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (sinceISO) qs.set('since', sinceISO);
      if (untilISO) qs.set('until', untilISO);
      const suffix = qs.toString() ? `?${qs.toString()}` : '';
      return api<StateHistoryResponse>(
        `/devices/${encodeURIComponent(thing!)}/state-history${suffix}`,
      );
    },
    enabled: Boolean(thing) && Boolean(sinceISO),
    refetchInterval: 60_000,
    // Keep the previous frame visible while a new window fetches so brush /
    // pan / range switches don't flash an empty chart between requests.
    placeholderData: keepPreviousData,
  });
}

export interface TopView {
  view: string;
  visits: number;
  total_dwell_ms: string;
  avg_dwell_ms: string;
}

export interface SessionDay {
  day: string;
  sessions: number;
  total_active_s: string;
  avg_active_s: string;
  total_views: number;
  feedback_count: number;
}

export interface ContentRow {
  content_id: string;
  kind: string;
  plays: number;
  total_watched_ms: string;
  avg_watched_ms: string;
  completion_rate: string | null;
}

/**
 * Optional fleet-wide filters for the Analytics page. `things` carries
 * the list of device ids resolved from a chosen device-group (groups
 * live in AWS IoT, so the SPA fetches via useDeviceGroups() and passes
 * the resulting thing list here). `tenant` is honoured by the backend
 * only for super-admins; other roles stay locked to their JWT tenant.
 */
export interface AnalyticsFilters {
  things?: string[];
  tenant?: string;
}

function thingQs(thing: string | undefined): string {
  return thing ? `&thing=${encodeURIComponent(thing)}` : '';
}

function filtersQs(filters: AnalyticsFilters | undefined): string {
  if (!filters) return '';
  const parts: string[] = [];
  if (filters.things && filters.things.length > 0) {
    parts.push(`things=${filters.things.map(encodeURIComponent).join(',')}`);
  }
  if (filters.tenant) {
    parts.push(`tenant=${encodeURIComponent(filters.tenant)}`);
  }
  return parts.length > 0 ? `&${parts.join('&')}` : '';
}

// Stable cache key for the filter object so React Query doesn't refetch
// on every render. `things` is sorted+joined so order doesn't trigger
// an invalidation.
function filtersKey(filters: AnalyticsFilters | undefined) {
  if (!filters) return null;
  const things =
    filters.things && filters.things.length > 0
      ? [...filters.things].sort().join(',')
      : null;
  return { things, tenant: filters.tenant ?? null };
}

export function useAnalyticsTopViews(
  days = 30,
  thing?: string,
  filters?: AnalyticsFilters,
) {
  return useQuery({
    queryKey: ['analytics', 'top-views', days, thing ?? null, filtersKey(filters)],
    queryFn: () =>
      api<{ window_days: number; top_views: TopView[] }>(
        `/analytics/top-views?days=${days}${thingQs(thing)}${filtersQs(filters)}`,
      ),
    refetchInterval: 60_000,
  });
}

export function useAnalyticsSessions(
  days = 30,
  thing?: string,
  filters?: AnalyticsFilters,
) {
  return useQuery({
    queryKey: ['analytics', 'sessions', days, thing ?? null, filtersKey(filters)],
    queryFn: () =>
      api<{ window_days: number; sessions_by_day: SessionDay[] }>(
        `/analytics/sessions?days=${days}${thingQs(thing)}${filtersQs(filters)}`,
      ),
    refetchInterval: 60_000,
  });
}

export interface SourceRow {
  source: string;
  transitions: number;
  total_dwell_s: string;
  avg_dwell_s: string;
}

export function useAnalyticsSources(
  days = 30,
  thing?: string,
  filters?: AnalyticsFilters,
) {
  return useQuery({
    queryKey: ['analytics', 'sources', days, thing ?? null, filtersKey(filters)],
    queryFn: () =>
      api<{ window_days: number; sources: SourceRow[] }>(
        `/analytics/source-attribution?days=${days}${thingQs(thing)}${filtersQs(filters)}`,
      ),
    refetchInterval: 60_000,
  });
}

export function useAnalyticsContent(
  days = 30,
  thing?: string,
  filters?: AnalyticsFilters,
) {
  return useQuery({
    queryKey: ['analytics', 'content', days, thing ?? null, filtersKey(filters)],
    queryFn: () =>
      api<{ window_days: number; content: ContentRow[] }>(
        `/analytics/content?days=${days}${thingQs(thing)}${filtersQs(filters)}`,
      ),
    refetchInterval: 60_000,
  });
}

// ─── Product engagement (v2.1.0 typed events) ───────────────────────────

export interface ProductRow {
  product_model: string;
  /**
   * Carousel-source product_view events with dwell_ms ≥ backend's
   * IMPRESSION_THRESHOLD_MS (2 s). Cards the user paged past on the way
   * to another product (typically <2 s focus) are counted as `sweeps`
   * instead, so the headline impression number reflects real attention
   * rather than transient pass-throughs.
   */
  impressions: number;
  /** Carousel events with dwell_ms below the impression threshold. */
  sweeps: number;
  detail_views: number;
  total_carousel_dwell_ms: string;
  total_detail_dwell_ms: string;
  avg_detail_dwell_ms: string;
  popup_count: number;
  gallery_count: number;
}

export interface CompareAggregate {
  views: number;
  total_dwell_ms: string;
  avg_dwell_ms: string;
}

export interface AnalyticsProductsResponse {
  window_days: number;
  products: ProductRow[];
  compare: CompareAggregate;
}

export function useAnalyticsProducts(
  days = 30,
  thing?: string,
  filters?: AnalyticsFilters,
) {
  return useQuery({
    queryKey: ['analytics', 'products', days, thing ?? null, filtersKey(filters)],
    queryFn: () =>
      api<AnalyticsProductsResponse>(
        `/analytics/products?days=${days}${thingQs(thing)}${filtersQs(filters)}`,
      ),
    refetchInterval: 60_000,
  });
}

export interface FeatureRow {
  pill_id: string;
  pill_label: string;
  views: number;
  total_dwell_ms: string;
  avg_dwell_ms: string;
}

export interface AnalyticsProductFeaturesResponse {
  window_days: number;
  product_model: string;
  features: FeatureRow[];
}

export function useAnalyticsProductFeatures(
  productModel: string | null,
  days = 30,
  thing?: string,
  filters?: AnalyticsFilters,
) {
  return useQuery({
    queryKey: [
      'analytics',
      'product-features',
      productModel,
      days,
      thing ?? null,
      filtersKey(filters),
    ],
    queryFn: () =>
      api<AnalyticsProductFeaturesResponse>(
        `/analytics/products/${encodeURIComponent(productModel!)}/features?days=${days}${thingQs(thing)}${filtersQs(filters)}`,
      ),
    enabled: Boolean(productModel),
    refetchInterval: 60_000,
  });
}

// ─── Per-device: settings + commands + telemetry history ────────────────

export type SettingsCategory =
  | 'audio'
  | 'security'
  | 'usb'
  | 'device_info'
  | 'debug'
  // Gateway-owned (DE-287): applied by uc-bridge, never sent to the uC.
  | 'features'
  | 'uc_logs';

export type HwModel = 'S1' | 'HW231' | 'HW232';

export interface SettingDescriptor {
  key: string;
  type: 'int' | 'bool' | 'enum' | 'text' | 'text[]';
  range?: { min: number; max: number; step?: number };
  enum?: Array<{ value: number | string; label: string }>;
  hw_models?: HwModel[];
  write_only?: boolean;
  read_only?: boolean;
  description?: string;
}

export interface SettingsResponse {
  catalog: Record<SettingsCategory, SettingDescriptor[]>;
  reported: Record<SettingsCategory, Record<string, unknown> | null>;
  /** Always null here — see useDeviceSettingsState for the live halves. */
  desired: null;
}

/**
 * Both shadow halves, live, from one GetThingShadow in the admin Lambda
 * (DE-287). The in-VPC backend cannot serve `desired` at all: the
 * event-dispatcher deliberately drops desired-only shadow updates, so RDS
 * never holds it. Reading both halves from the same response also removes a
 * whole class of skew bugs — a cached `reported` next to a live `desired`
 * disagrees for reasons that have nothing to do with the device.
 */
export interface StuckSetting {
  key: string;
  desired: unknown;
  reported: unknown;
}

export interface SettingsStateResponse {
  thing: string;
  desired: Partial<Record<SettingsCategory, Record<string, unknown>>>;
  reported: Partial<Record<SettingsCategory, Record<string, unknown>>>;
  /** Desired keys `reported` does not match — see useStuckSettingsBatch. */
  stuck: StuckSetting[];
  fetchedAt: string;
}

export interface UcLogRecord {
  ts: number;
  level: number | null;
  module: string | null;
  msg: string | null;
  /** uC ms-since-boot — not comparable across uC reboots. */
  uptimeMs: number | null;
  repeat: number | null;
  bootSession: number | null;
}

export interface UcLogsResponse {
  thing: string;
  group: string;
  records: UcLogRecord[];
  /** More matched than the page budget allowed — older ones were not read. */
  truncated: boolean;
  fetchedAt: string;
}

/**
 * The uC's own WARN/ERROR stream, read from CloudWatch (DE-287).
 *
 * Only levels 3 and 4 are ever here: the device streams nothing lower, and
 * records below the floor are kept on-device for a log bundle instead.
 */
export function useUcLogs(
  thing: string | undefined,
  opts: { hours: number; minLevel?: number },
) {
  return useQuery({
    queryKey: ['admin', 'uc-logs', thing, opts.hours, opts.minLevel ?? 0],
    enabled: Boolean(thing),
    queryFn: () => {
      const params = new URLSearchParams({
        start: String(Date.now() - opts.hours * 3600_000),
        limit: '200',
      });
      if (opts.minLevel) params.set('minLevel', String(opts.minLevel));
      return api<UcLogsResponse>(
        `/admin/devices/${encodeURIComponent(thing!)}/uc-logs?${params}`,
      );
    },
    refetchInterval: 60_000,
    retry: false,
  });
}

export interface UcLogBundle {
  jobId: string;
  key: string;
  bytes: number | null;
  files: number | null;
  truncated: boolean;
  completedAt: string | null;
  requestedBy: string | null;
}

export function useUcLogBundles(thing: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'uc-log-bundles', thing],
    enabled: Boolean(thing),
    queryFn: () =>
      api<{ thing: string; bundles: UcLogBundle[]; fetchedAt: string }>(
        `/admin/devices/${encodeURIComponent(thing!)}/uc-log-bundles`,
      ),
    refetchInterval: 60_000,
    retry: false,
  });
}

/**
 * Ask a device for a log bundle (super-admin).
 *
 * Bounded on purpose: the device is usually on a metered SIM, so the default
 * window is small and the NEWEST records win. The job is dispatched by the
 * in-VPC backend, which mints the presigned PUT the device uploads to.
 */
export function useRequestUcLogBundle() {
  const qc = useQueryClient();
  return useMutation<
    { thing: string; jobId: string; key: string; expiresAt: number },
    Error,
    { thing: string; sinceHours: number; maxBytes: number }
  >({
    mutationFn: ({ thing, sinceHours, maxBytes }) =>
      api(`/devices/${encodeURIComponent(thing)}/uc-logs-bundle`, {
        method: 'POST',
        body: JSON.stringify({ sinceHours, maxBytes }),
      }),
    onSuccess: (_d, vars) => {
      // The job appears in DeviceUpdateJobs; kick both so the operator sees
      // something happen rather than waiting out a poll interval.
      void qc.invalidateQueries({ queryKey: ['admin', 'device-update-jobs', vars.thing] });
      void qc.invalidateQueries({ queryKey: ['admin', 'uc-log-bundles', vars.thing] });
    },
  });
}

/** A short-lived download URL for one bundle, fetched on click. */
export async function fetchUcLogBundleUrl(thing: string, key: string) {
  const r = await api<{ url: string }>(
    `/admin/devices/${encodeURIComponent(thing)}/uc-log-bundles?key=${encodeURIComponent(key)}`,
  );
  return r.url;
}

export interface StuckSettingsBatchResponse {
  fetchedAt: string;
  devices: Record<string, { stuck: StuckSetting[] }>;
}

/**
 * Fleet sweep for desired keys that never converge (super-admin).
 *
 * Worth surfacing rather than leaving to chance: AWS re-publishes the shadow
 * delta on every shadow update while desired differs from reported, so one
 * key a device cannot apply keeps costing uplink messages on a metered SIM
 * until somebody clears it.
 */
export function useStuckSettingsBatch(things: string[], enabled = true) {
  const key = [...things].sort().join(',');
  return useQuery({
    queryKey: ['admin', 'settings-stuck', 'batch', key],
    enabled: enabled && things.length > 0,
    queryFn: () =>
      api<StuckSettingsBatchResponse>('/admin/settings-stuck/batch', {
        method: 'POST',
        body: JSON.stringify({ things: things.slice(0, 100) }),
      }),
    refetchInterval: 300_000,
    staleTime: 290_000,
    retry: false,
  });
}

export function useDeviceSettingsState(thing: string | undefined) {
  return useQuery({
    queryKey: ['device-settings-state', thing],
    queryFn: () =>
      api<SettingsStateResponse>(
        `/admin/devices/${encodeURIComponent(thing!)}/settings-state`,
      ),
    enabled: Boolean(thing),
    // A write converges within seconds when the device is online, so poll
    // faster than the catalog query; a pending row should not sit stale.
    refetchInterval: 10_000,
    retry: false,
  });
}

export function useDeviceSettings(thing: string | undefined) {
  return useQuery({
    queryKey: ['device-settings', thing],
    queryFn: () => api<SettingsResponse>(`/devices/${encodeURIComponent(thing!)}/settings`),
    enabled: Boolean(thing),
    refetchInterval: 30_000,
  });
}

/**
 * uC `STATE_DESIRED_RESP` error codes (spec §8.8). Translated by the UI
 * into the human-readable error pinned underneath a failed settings row.
 */
export const SETTINGS_ERROR_LABEL: Record<number, string> = {
  1: 'general error',
  2: 'invalid parameter',
  3: 'out of range',
  4: 'not supported in this state',
  5: 'BLE busy',
  6: 'unknown key',
  7: 'read-only',
  8: 'PIN required',
};

export interface SetSettingsVars {
  thing: string;
  category: SettingsCategory;
  /** Field-key → new value, written to the shadow's `desired` half. */
  delta: Record<string, unknown>;
}

/**
 * 202, not 200. The write only records intent: the device applies it when it
 * next sees the shadow delta, which for an offline device is whenever it comes
 * back. There is no applied/failed map here — success means "accepted and
 * written to desired", and the outcome shows up as `reported` converging, or
 * as a settings_rejected event (see useSettingsRejections).
 */
export interface SetSettingsResponse {
  thing: string;
  category: SettingsCategory;
  desired: Record<string, unknown>;
  requestedBy: string;
}

/**
 * Per-key rejections, read from the device's own event feed.
 *
 * The uC answers a STATE_DESIRED with a §8.9 `failed` map, but that response
 * never leaves the device — iot-gateway forwards events, logs, command
 * responses and shadow updates, nothing else. uc-bridge therefore republishes
 * the outcome as a settings_rejected event, which is how a refused key reaches
 * the Portal at all.
 */
export function useSettingsRejections(thing: string | undefined) {
  const events = useDeviceEvents(thing, 50);
  const rejections = new Map<string, { code: number; ts: string }>();
  for (const row of events.data?.events ?? []) {
    if (row.event_type !== 'settings_rejected') continue;
    const failed = (row.data as { failed?: Record<string, unknown> }).failed;
    if (!failed || typeof failed !== 'object') continue;
    for (const [key, code] of Object.entries(failed)) {
      // Newest wins: the feed is newest-first, so only take the first sighting.
      if (typeof code === 'number' && !rejections.has(key)) {
        rejections.set(key, { code, ts: row.ts });
      }
    }
  }
  return rejections;
}

/**
 * Write one category's settings to the shadow's desired half (DE-287).
 *
 * The route lives in the admin API Lambda rather than the in-VPC backend
 * because the backend cannot reach the IoT data plane at all — the account has
 * only the iot.api control-plane endpoint and the S3 gateway endpoint.
 *
 * Nothing optimistic happens here. The previous mock wrote the new value
 * straight into the cached `reported` section, which made a row look applied
 * before any device had seen it; with a real device in the loop that would be
 * a lie for as long as the device is offline. The row instead goes pending and
 * waits for `reported` to catch up with `desired`.
 */
export function useSetDeviceSettings() {
  const qc = useQueryClient();
  return useMutation<SetSettingsResponse, Error, SetSettingsVars>({
    mutationFn: (vars) =>
      api<SetSettingsResponse>(
        `/admin/devices/${encodeURIComponent(vars.thing)}/settings/${encodeURIComponent(
          vars.category,
        )}`,
        { method: 'PUT', body: JSON.stringify(vars.delta) },
      ),
    onSuccess: (_data, vars) => {
      // Pull the freshly-written desired straight away so the row can show
      // what it is waiting for, instead of waiting out the poll interval.
      void qc.invalidateQueries({ queryKey: ['device-settings-state', vars.thing] });
      void qc.invalidateQueries({ queryKey: ['device-events', vars.thing] });
    },
  });
}

export type CommandGroup = 'power' | 'audio' | 'source' | 'system' | 'menu';

export interface CommandParam {
  name: string;
  type: 'int' | 'enum' | 'bool';
  enum?: Array<{ value: number | string; label: string }>;
  min?: number;
  max?: number;
  step?: number;
  optional?: boolean;
}

export interface CommandDescriptor {
  cmd: string;
  group: CommandGroup;
  label: string;
  params?: CommandParam[];
}

export interface CommandsResponse {
  catalog: CommandDescriptor[];
  recent: unknown[];
}

export function useDeviceCommands(thing: string | undefined) {
  return useQuery({
    queryKey: ['device-commands', thing],
    queryFn: () => api<CommandsResponse>(`/devices/${encodeURIComponent(thing!)}/commands`),
    enabled: Boolean(thing),
    refetchInterval: 60_000,
  });
}

export type TelemetryBucket = '5m' | '1h' | '1d';

export interface TelemetryPoint {
  bucket: string;
  cpu_percent_avg: number | null;
  cpu_temp_c_max: number | null;
  mem_used_mb_avg: number | null;
  modem_signal_max: number | null;
  throttled_any: boolean | null;
  proj_lamp_hrs_max: number | null;
  proj_op_hrs_max: number | null;
  proj_temp_intake_avg: number | null;
  proj_temp_exhaust_avg: number | null;
  proj_temp_light1_max: number | null;
  proj_temp_light2_max: number | null;
  sensor_temp_int_avg: number | null;
  sensor_humidity_avg: number | null;
  remote_battery_min: number | null;
}

export interface TelemetryHistoryResponse {
  bucket: TelemetryBucket;
  hours: number;
  points: TelemetryPoint[];
}

export function useDeviceTelemetry(
  thing: string | undefined,
  bucket: TelemetryBucket = '5m',
  hours = 6,
) {
  const refetch =
    bucket === '5m' ? 30_000 : bucket === '1h' ? 5 * 60_000 : 60 * 60_000;
  return useQuery({
    queryKey: ['device-telemetry', thing, bucket, hours],
    queryFn: () =>
      api<TelemetryHistoryResponse>(
        `/devices/${encodeURIComponent(thing!)}/telemetry?bucket=${bucket}&hours=${hours}`,
      ),
    enabled: Boolean(thing),
    refetchInterval: refetch,
    // Keep the previous frame visible while a new window fetches so brush /
    // pan / range switches don't flash an empty chart between requests.
    placeholderData: keepPreviousData,
  });
}

// ─── Admin: users ──────────────────────────────────────────────────────

export interface AdminUser {
  username: string;
  email: string | null;
  tenant_id: string | null;
  role: 'super-admin' | 'tenant-admin' | 'tenant-viewer' | null;
  status: string | null;
  created: string | null;
  enabled: boolean;
  // DE-146 — opt-out toggle for the unassigned-device digest email.
  // Missing Cognito attribute is treated as `true` (opted in by default).
  notify_unassigned: boolean;
  // Derived from email domain by the admin Lambda. Artome users sign in via
  // Microsoft federation and have no usable Cognito password; UI hides the
  // change/reset password actions for these rows.
  auth_method: 'microsoft' | 'password';
  // UTC ISO 8601 timestamp stamped by the PostAuthentication Lambda on every
  // successful sign-in. Null = user has never signed in (or signed in before
  // the trigger was deployed).
  last_signed_in: string | null;
  // DE-231 — last USE of the Portal, which is the question the Users page is
  // actually asking. Stamped by the PreTokenGeneration Lambda on sign-in AND
  // token refresh, and by the POST /admin/activity heartbeat while a tab is
  // open. The admin Lambda falls back to last_signed_in when no activity has
  // been recorded yet, so this is never worse than the old column.
  last_activity: string | null;
}

export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<{ users: AdminUser[] }>('/admin/users'),
  });
}

export function useInviteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; role: AdminUser['role']; tenant_id: string }) =>
      api<{ user: AdminUser }>('/admin/users', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (username: string) =>
      api<void>(`/admin/users/${encodeURIComponent(username)}`, {
        method: 'DELETE',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// DE-140 — change a user's role. Backend privilege guards mirrored in the SPA.
export function useChangeRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, role }: { username: string; role: NonNullable<AdminUser['role']> }) =>
      api<{ username: string; role: string }>(
        `/admin/users/${encodeURIComponent(username)}/role`,
        {
          method: 'PUT',
          body: JSON.stringify({ role }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// DE-141 — admin-triggered password reset (Cognito sends a reset email).
export function useResetPassword() {
  return useMutation({
    mutationFn: (username: string) =>
      api<{ username: string; reset: boolean }>(
        `/admin/users/${encodeURIComponent(username)}/reset-password`,
        { method: 'POST' },
      ),
  });
}

// DE-141 — re-send invitation email to a user still in FORCE_CHANGE_PASSWORD.
export function useResendInvite() {
  return useMutation({
    mutationFn: (username: string) =>
      api<{ username: string; invited: boolean }>(
        `/admin/users/${encodeURIComponent(username)}/resend-invite`,
        { method: 'POST' },
      ),
  });
}

// DE-142 — toggle Cognito Enabled (admin disable / re-enable).
export function useSetUserEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, enabled }: { username: string; enabled: boolean }) =>
      api<{ username: string; enabled: boolean }>(
        `/admin/users/${encodeURIComponent(username)}/enabled`,
        {
          method: 'PUT',
          body: JSON.stringify({ enabled }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// DE-146 — update a user's notification preferences. The backend allows
// self-edit by anyone (even tenant-viewer); cross-user edit requires super-admin
// or a tenant-admin on the same tenant. UI guards mirror those rules.
export function useUpdateNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ username, notify_unassigned }: { username: string; notify_unassigned: boolean }) =>
      api<{ username: string; notify_unassigned: boolean }>(
        `/admin/users/${encodeURIComponent(username)}/notification-preferences`,
        {
          method: 'PUT',
          body: JSON.stringify({ notify_unassigned }),
        },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  });
}

// ─── Admin: IoT thing-group membership (super-admin only) ──────────────

export const ARTOMEUI_ENV_GROUPS = [
  'artomeui-unassigned',
  'artomeui-dev',
  'artomeui-demo',
  'artomeui-prod',
] as const;
export type EnvGroup = (typeof ARTOMEUI_ENV_GROUPS)[number];

export const ENV_GROUP_LABEL: Record<EnvGroup, string> = {
  'artomeui-unassigned': 'Unassigned',
  'artomeui-dev': 'Dev',
  'artomeui-demo': 'Demo',
  'artomeui-prod': 'Prod',
};

export interface GroupComponent {
  name: string;
  version: string;
}

export interface GroupDeployment {
  deployment_id: string;
  deployment_name: string | null;
  revision_id: string;
  components: GroupComponent[];
  /** Absent on a pre-DE-279 Lambda — consumers must tolerate undefined. */
  iot_job_id?: string | null;
}

// Active Mender IoT Job rolling out to a thing-group. Pre-filtered server-side
// to IN_PROGRESS jobs only — once terminal, a rollout vanishes from the card
// because COMPLETED/CANCELED jobs don't apply to existing or future members.
export interface MenderDeploymentCounts {
  queued: number;
  in_progress: number;
  succeeded: number;
  failed: number;
  rejected: number;
  canceled: number;
  timed_out: number;
  removed: number;
}

export interface MenderDeployment {
  job_id: string;
  artifact_name: string | null;
  created_at: string;
  status: 'IN_PROGRESS';
  counts: MenderDeploymentCounts;
}

export interface DeviceGroupCard {
  name: EnvGroup;
  devices: string[];
  /**
   * DE-310 — thing → the `deviceType` THING ATTRIBUTE (DE-308), where one
   * exists. The attribute, not the shadow: a freshly provisioned unit has it
   * from its first registration but has no shadow until telemetry runs, and a
   * guard reading only the database would count it as the fleet default.
   * Members are OMITTED rather than defaulted — see lib/hardware.ts.
   */
  device_types?: Record<string, string>;
  deployment: GroupDeployment | null;
  // Always an array — empty when no Mender rollout is in flight against this
  // group's thing-group ARN. Newest first.
  mender_active: MenderDeployment[];
}

export function useDeviceGroups() {
  return useQuery({
    queryKey: ['admin', 'device-groups'],
    queryFn: () => api<{ groups: DeviceGroupCard[] }>('/admin/device-groups'),
  });
}

// DE-132 — registry-info fallback when /devices/:id 404s because the device
// hasn't published a shadow yet (typical for fresh artomeui-unassigned things).
// Returns Thing identity + group membership + certificate fingerprints from
// the AWS IoT registry directly.
export interface DeviceRegistryInfo {
  thing: string;
  arn: string | null;
  thing_type: string | null;
  attributes: Record<string, string>;
  groups: string[];
  env_group: EnvGroup | null;
  certificates: Array<{ id: string; arn: string }>;
  registry_version: number | null;
}

export function useDeviceRegistryInfo(thing: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['admin', 'device-registry-info', thing],
    queryFn: () =>
      api<DeviceRegistryInfo>(
        `/admin/devices/${encodeURIComponent(thing!)}/registry-info`,
      ),
    enabled: Boolean(thing) && enabled,
    // Registry info changes rarely; one-shot fetch with default staleTime is fine.
    retry: false,
  });
}

// Per-device ACTIVE update jobs (admin Lambda; tenant-scoped). The in-flight OS
// (Mender) / content / Greengrass update jobs landing on one device — QUEUED +
// IN_PROGRESS in `active`, plus (DE-276) `staged`: terminal SUCCEEDED
// executions whose payload the device only staged (applies at next power-on).
// `greengrass` is the env-group's latest deployment, attached only when a GG
// rollout is in flight (reuses GroupDeployment).
export type UpdateJobKind =
  | 'mender' | 'content' | 'greengrass' | 'release' | 'reboot'
  | 'uc_logs_bundle' | 'other';

// DE-171 2E — parts a unified `release` job carries (+ versions from the job doc);
// null on non-release kinds. Mirrors the admin Lambda's ReleaseParts.
export interface ReleaseParts {
  os: string | null;
  content: boolean;
  app: string | null;
  ucfw: string | null;
}

export interface DeviceUpdateJob {
  job_id: string;
  kind: UpdateJobKind;
  /** QUEUED/IN_PROGRESS in `active`; SUCCEEDED only in `staged` (DE-276). */
  status: 'QUEUED' | 'IN_PROGRESS' | 'SUCCEEDED';
  title: string | null;
  /** release only — which parts the bundle carries + versions; null otherwise. */
  parts: ReleaseParts | null;
  queued_at: string | null;
  started_at: string | null;
  last_updated_at: string | null;
  status_details: Record<string, string> | null;
}

export interface DeviceUpdateJobs {
  thing: string;
  env_group: EnvGroup | null;
  active: DeviceUpdateJob[];
  /** DE-276 — staged-but-not-applied terminal jobs, newest per kind. Absent on
   * a pre-DE-276 Lambda, so consumers must tolerate undefined. */
  staged?: DeviceUpdateJob[];
  greengrass: GroupDeployment | null;
}

export function useDeviceUpdateJobs(thing: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'device-update-jobs', thing],
    queryFn: () =>
      api<DeviceUpdateJobs>(`/admin/devices/${encodeURIComponent(thing!)}/update-jobs`),
    enabled: Boolean(thing),
    refetchInterval: 15_000, // active jobs transition; surface stage changes live (tab-hidden auto-pauses)
    retry: false, // a 403/404 shouldn't hammer; matches useDeviceRegistryInfo
  });
}

// DE-139 — list every tenant the system knows about. Backend's
// /admin/tenants only returns tenants that have at least one device; we
// also need tenants that exist via users (e.g. demo-customer seeded with
// users but no device yet), so we union the two on the client. Super-
// admin only — both source endpoints 403 otherwise.
//
// Returns the union as `tenants: string[]`, sorted, deduped.
export function useTenants() {
  const fromDevices = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => api<{ tenants: string[] }>('/admin/tenants'),
  });
  const fromUsers = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => api<{ users: AdminUser[] }>('/admin/users'),
  });
  const tenants = (() => {
    const set = new Set<string>();
    for (const t of fromDevices.data?.tenants ?? []) set.add(t);
    for (const u of fromUsers.data?.users ?? []) {
      if (u.tenant_id) set.add(u.tenant_id);
    }
    return [...set].sort();
  })();
  return {
    data: { tenants },
    isLoading: fromDevices.isLoading || fromUsers.isLoading,
    error: fromDevices.error ?? fromUsers.error,
  };
}

// DE-139 — reassign a device to a different tenant. Backend writes
// devices.tenant_id; RLS visibility for users in the new tenant updates
// on their next refetch. No IoT Thing-attribute mirror (RDS is the
// single source of truth).
export function useSetDeviceTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ thing, tenant_id }: { thing: string; tenant_id: string }) =>
      api<{ thing: string; tenant_id: string }>(
        `/admin/devices/${encodeURIComponent(thing)}/tenant`,
        {
          method: 'PUT',
          body: JSON.stringify({ tenant_id }),
        },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['devices'] });
      qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
  });
}

// Set the operator-typed human label for a device. Backend RLS enforces
// scope: tenant-admin updates succeed only for their own tenant,
// super-admin for any device, tenant-viewer is rejected at the app layer.
// Pass friendly_name: null (or '') to clear the name.
export function useSetDeviceFriendlyName() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ thing, friendly_name }: { thing: string; friendly_name: string | null }) =>
      api<{ thing: string; friendly_name: string | null }>(
        `/admin/devices/${encodeURIComponent(thing)}/friendly-name`,
        {
          method: 'PUT',
          body: JSON.stringify({ friendly_name }),
        },
      ),
    onSuccess: (_data, vars) => {
      // Invalidate both the list (Dashboard, OpsDevices) and the open
      // detail view so the new name appears everywhere on next render.
      qc.invalidateQueries({ queryKey: ['devices'] });
      qc.invalidateQueries({ queryKey: ['device', vars.thing] });
    },
  });
}

export function useSetDeviceGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ thing, group }: { thing: string; group: EnvGroup }) =>
      api<{ thing: string; env_group: EnvGroup }>(
        `/admin/devices/${encodeURIComponent(thing)}/env-group`,
        {
          method: 'PUT',
          body: JSON.stringify({ group }),
        },
      ),
    // DE-131 — AWS IoT thing-group membership is eventually consistent. A
    // refetch within ~5–15s of the mutation can return STALE membership,
    // putting the device back into its old group and causing a visible
    // flicker. Two-step strategy:
    //
    //   1. Optimistic cache update on mutate → instant visual move.
    //   2. After mutation success, poll /admin/device-groups until the
    //      response reflects our intent (device IS in target group), then
    //      adopt that response as the new cache state. If it never
    //      converges within the cap, fall back to a plain invalidate.
    //
    // Rolls back via onError if the mutation itself failed.
    onMutate: async ({ thing, group: target }) => {
      const key = ['admin', 'device-groups'] as const;
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<{ groups: DeviceGroupCard[] }>(key);
      if (previous) {
        qc.setQueryData<{ groups: DeviceGroupCard[] }>(key, {
          ...previous,
          groups: previous.groups.map((g) => ({
            ...g,
            devices:
              g.name === target
                ? Array.from(new Set([...g.devices, thing])).sort()
                : g.devices.filter((t) => t !== thing),
          })),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData(['admin', 'device-groups'], ctx.previous);
      }
    },
    onSuccess: async (_data, { thing, group: target }) => {
      const key = ['admin', 'device-groups'] as const;
      const matches = (data: { groups: DeviceGroupCard[] } | undefined) =>
        data?.groups.find((g) => g.name === target)?.devices.includes(thing) ?? false;
      // Poll every 2s for up to 30s. Stop on first converged read.
      for (let attempt = 0; attempt < 15; attempt++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const fresh = await api<{ groups: DeviceGroupCard[] }>('/admin/device-groups');
          if (matches(fresh)) {
            qc.setQueryData(key, fresh);
            qc.invalidateQueries({ queryKey: ['devices'] });
            return;
          }
        } catch {
          // try again on next iteration
        }
      }
      // Never converged — fall back to an invalidate so the cache catches
      // up on the next natural refetch instead of staying optimistically stale.
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['devices'] });
    },
  });
}

// ─── Content service (DE-171, Phase 2B) ─────────────────────────────────
//
// Item-centric media library + immutable auto-versioned profiles + FLEET
// deploy. The library groups files into logical items (a testimonial = its
// .mp4+.jpg+.json; a song = .mp3+.png) so a triplet moves/deploys as one unit.
// Deploying a profile targets an AWS IoT THING-GROUP (one SNAPSHOT content_sync
// job + a content_group_deployments ledger row) or a single device (per-thing
// override). Writes are super-admin-gated server-side (RLS + requireRole); the
// SPA mirrors that. Group content-job STATUS is read from the admin Lambda
// (outside the VPC, has IoT job perms); the writer stays in the VPC backend.

export interface ContentAsset {
  id: string;
  collection: string;
  relpath: string;
  s3_key: string;
  sha256: string;
  bytes: string;          // bigint → string from pg
  duration_s: number | null;
  kind: string | null;
  title: string | null;
  video_codec: string | null;  // ffprobe codec_name (h264|hevc); null for non-video / pre-migration
  created_at: string;
}

// A logical content item — all files sharing (collection, stem).
export interface ContentItem {
  itemId: string;             // "<collection>/<stem>"
  collection: string;
  stem: string;
  title: string;              // content_assets.title ?? stem
  description: string | null; // sidecar (testimonials' <stem>.json) description; null otherwise
  kind: string;               // primary kind (video|audio|image|sidecar)
  primaryAssetId: string;
  fileAssetIds: string[];
  totalBytes: number;
  duration_s: number | null;
  fileCount: number;
  complete: boolean;          // has all required files for its collection
  managed: boolean;           // processor-uploaded (has S3 bytes + a thumbnail)
  thumbnailUrl: string | null; // presigned GET (1h); null → UI placeholder
  videoCodec: string | null;  // primary video's ffprobe codec_name (h264|hevc); null for non-video / pre-migration
  // DE-234. `language` is the SPOKEN language of the item (for a video) — null
  // means UNKNOWN, and unknown is never treated as a guess: the device's
  // wrong-language filter skips it rather than hiding real content.
  language: string | null;
  // Which subtitle tracks the item HAS. Derived server-side from the grouped
  // files, so it cannot drift from what is actually registered.
  subtitleLangs: string[];
  /** Each track WITH its asset id, so a wrong subtitle can actually be removed
   *  — `subtitleLangs` gives language codes with nothing to address. */
  subtitleTracks: { language: string; assetId: string; relpath: string }[];
  titleI18n: Record<string, { title?: string; description?: string }> | null;
}

export interface ContentProfile {
  id: string;
  name: string;
  version: string;
  min_app_version: string | null;
  min_os_version: string | null;   // OS/Mender gate (semver); null = no gate
  created_at: string;
  asset_count: number;
  total_bytes: string | null;      // bigint → string from pg (DE-266 payload estimate)
}

export interface ContentProfileVersion {
  id: string;
  version: string;
  min_app_version: string | null;
  min_os_version: string | null;
  created_at: string;
  asset_count: number;
}
export interface ProfileVersionsResp {
  versions: ContentProfileVersion[];
  deployedTo: { group_name: string; profile_id: string; profile_version: string; assigned_at: string }[];
}

// A single profile + its logical items — seeds the "Copy profile" Compose form.
export interface ProfileDetail {
  id: string;
  name: string;
  version: string;
  minAppVersion: string | null;
  minOsVersion: string | null;
  itemIds: string[];
}

// Applied = shadow.reported.content (telemetry, whole-category write);
// assigned = the device's override if any, else its group's deployment (`via`).
export interface DeviceContent {
  applied: {
    /** Headline profile identity ({name, version}); null/absent on a device whose
     * content predates the identity feature. What contentSyncStatus compares. */
    appliedProfile?: { name?: string | null; version?: string | null } | null;
    collections?: Record<
      string,
      {
        profile?: string;
        version?: string | null;
        profileVersion?: string | null;
        managed?: boolean;
        fileCount?: number;
      }
    >;
  } | null;
  assigned:
    | { profile_version: string; profile_name: string; resolved_at: string; via?: string }
    | null;
}

export function useContentAssets() {
  return useQuery({
    queryKey: ['content', 'assets'],
    queryFn: () => api<{ assets: ContentAsset[] }>('/content/assets'),
  });
}

export function useContentItems() {
  return useQuery({
    queryKey: ['content', 'items'],
    queryFn: () => api<{ items: ContentItem[] }>('/content/items'),
    // Thumbnails are presigned for 1h; keep cached rows well under that so an
    // <img> never points at an expired URL (the card also has onError fallback).
    staleTime: 30 * 60_000,
  });
}

export function useContentProfiles() {
  return useQuery({
    queryKey: ['content', 'profiles'],
    queryFn: () => api<{ profiles: ContentProfile[] }>('/content/profiles'),
  });
}

export function useProfileVersions(name: string | undefined) {
  return useQuery({
    queryKey: ['content', 'profile-versions', name],
    queryFn: () => api<ProfileVersionsResp>(`/content/profiles/${encodeURIComponent(name!)}/versions`),
    enabled: Boolean(name),
  });
}

// A profile's detail + item ids (for "Copy profile"). Disabled until an id is set.
export function useProfileDetail(id: string | null) {
  return useQuery({
    queryKey: ['content', 'profile-detail', id],
    queryFn: () => api<ProfileDetail>(`/content/profiles/${encodeURIComponent(id!)}`),
    enabled: Boolean(id),
  });
}

// version optional → backend derives the next integer for the name.
export function useCreateContentProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      version?: string;
      minAppVersion?: string;
      minOsVersion?: string;
      assetIds: string[];
    }) =>
      api<{ id: string; version: string }>('/content/profiles', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'profiles'] });
      qc.invalidateQueries({ queryKey: ['content', 'profile-versions'] });
    },
  });
}

// DE-171 2E — content DEPLOY moved to the Releases page (useDeployRelease); the
// old useDeployContent / useDeployPreview (POST /content/assignments,
// /content/deploy-preview) are retired. The Content page is authoring-only.

// Per-device applied-vs-assigned (DeviceContentCard). `group` (the device's
// env-group) lets the backend surface a group-inherited assigned profile from
// the release_deployments ledger when there's no device-specific release.
export function useDeviceContent(thing: string | undefined, group?: string | null) {
  return useQuery({
    queryKey: ['content', 'device', thing, group ?? null],
    queryFn: () =>
      api<DeviceContent>(
        `/devices/${encodeURIComponent(thing!)}/content${group ? `?group=${encodeURIComponent(group)}` : ''}`,
      ),
    enabled: Boolean(thing),
    refetchInterval: 15_000,
  });
}

// Device-truth content drill-down (DE-171). The LITERAL files on a device
// (device_content_inventory, uplinked by telemetry), enriched against
// content_assets + flagged vs the assigned profile. Fetched LAZILY — only when
// a collection row is expanded (`enabled`) — so it doesn't ride the 15 s
// useDeviceContent poll. `group` lets the backend resolve a group-inherited
// assigned profile for the drift markers.
export interface DeviceContentFile {
  name: string;
  bytes: number;
  title: string | null;
  kind: string | null;
  duration_s: number | null;
  sha256: string | null;
  inProfile: boolean | null; // null when no assigned profile resolved
  assetId: string | null;    // matched content_assets.id → drives the ▶ play button (null = no match)
}
// A logical item on the device — files sharing a stem (testimonial =
// video+poster+text, song = audio+cover), grouped server-side. Keeps its raw
// files so the drill-down can expand to the literal parts (device-truth).
export interface DeviceContentItem {
  itemId: string;
  stem: string;
  title: string;
  kind: string | null;          // primary kind
  files: DeviceContentFile[];
  totalBytes: number;
  duration_s: number | null;
  partsPresent: string[];       // kinds present
  partsMissing: string[];       // required kinds absent → ghost slots
  completeOnDevice: boolean;
  anyExtra: boolean;            // a file present that's not in the assigned profile
  thumbnailUrl: string | null;  // presigned poster (video) / cover (audio); null → placeholder
}
export interface DeviceContentFileCollection {
  collection: string;
  items: DeviceContentItem[];   // default render unit (grouped)
  files: DeviceContentFile[];   // raw flat list (kept; parts shown on expand)
  missingFromDevice: string[]; // assigned-profile relpaths absent on the device
}
export interface DeviceContentFiles {
  reportedAt: string | null; // null → device hasn't reported an inventory yet
  collections: DeviceContentFileCollection[];
}

export function useDeviceContentFiles(
  thing: string | undefined,
  group: string | null | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['content', 'device-files', thing, group ?? null],
    queryFn: () => {
      const qs = group ? `?group=${encodeURIComponent(group)}` : '';
      return api<DeviceContentFiles>(`/devices/${encodeURIComponent(thing!)}/content/files${qs}`);
    },
    enabled: Boolean(thing) && enabled,
    staleTime: 60_000, // RDS-sourced; changes only when the device next reports
  });
}

// Presign a PUT into the upload-staging area. Uploads always land in `storage`;
// the content-processor Lambda validates+transcodes+registers them, then the
// operator Moves them into a device collection. The actual S3 PUT is a raw
// XMLHttpRequest in UploadModal (a presigned URL must not carry the Bearer
// header, and XHR exposes upload-progress events) — NOT this api() call.
export interface UploadPresign {
  uploadUrl: string;
  key: string;
  uploadId: string;       // content_uploads row to poll for the probe decision / status
}
export function useUploadPresign() {
  return useMutation({
    // format: 'h264' | 'hevc' (video) | 'audio' (audio) | 'sidecar' (.vtt/.json).
    //
    // For a SUBTITLE, itemId + language are REQUIRED and the server derives the
    // stored filename from them. It deliberately does not trust `filename`: a
    // subtitle only reaches its video when the name is exactly
    // `<stem>.<lang>.vtt`, and a mis-named one fails SILENTLY — it registers as
    // its own stray item and simply never reaches a device.
    mutationFn: (body: { filename: string; contentType?: string; format?: string;
                         itemId?: string; language?: string }) =>
      api<UploadPresign>('/content/uploads', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

// Server-side probe decision + lifecycle for an upload (polled by UploadModal). A
// re-encode parks at 'pending-confirmation' until the operator confirms — the gate.
export interface UploadStatus {
  id: string;
  status: 'uploading' | 'pending-confirmation' | 'confirmed' | 'done' | 'cancelled' | 'failed';
  decision: 'passthrough' | 'transcode' | null;
  reason: string | null;
  error: string | null;
  format: string;
  relpath: string;
  src_codec: string | null;
  src_width: number | null;
  src_height: number | null;
  duration_s: number | null;
}

// Presign an asset's media GET for inline playback (the preview lightbox).
// Click-triggered (never eager) — a fresh URL per open; 1 h expiry.
export interface MediaUrl {
  url: string;
  contentType: string;
}
export function useGetMediaUrl() {
  return useMutation({
    mutationFn: ({ assetId }: { assetId: string }) =>
      api<MediaUrl>(`/content/assets/${encodeURIComponent(assetId)}/media`),
  });
}

// Move a whole ITEM between collections (all its stem-sharing files together —
// the server moves them atomically so a triplet can't be split). DB-only.
export function useMoveItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, collection }: { itemId: string; collection: string }) =>
      api<{ itemId: string; collection: string; files: number }>('/content/items', {
        method: 'PATCH',
        body: JSON.stringify({ itemId, collection }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'assets'] });
    },
  });
}

/** Item metadata as the DEVICE reads it — the shape of `<stem>.json`. */
export interface ItemMetadata {
  title?: string | null;
  description?: string | null;
  language?: string | null;
  i18n?: Record<string, { title?: string; description?: string }> | null;
}

/**
 * Edit an item's metadata.
 *
 * The backend writes the columns AND regenerates the `<stem>.json` object in one
 * transaction — the file is what a device actually receives, so updating columns
 * alone would change what this page shows while every device kept the old bytes.
 * That divergence is what DE-247 exists to fix.
 *
 * PATCH semantics: an omitted field is left alone. Send an empty string to clear
 * one. This matters because a form that only knows about `title` must not blank
 * the German overrides it never rendered.
 */
export function useEditItemMetadata() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, ...body }: { itemId: string } & ItemMetadata) =>
      // itemId in the BODY: it is "<collection>/<stem>", and API Gateway decodes
      // %2F before routing, so it cannot be a path param.
      api<{ itemId: string; sidecarAssetId: string; sha256: string; doc: ItemMetadata }>(
        '/content/items/metadata',
        { method: 'PATCH', body: JSON.stringify({ itemId, ...body }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'assets'] });
    },
  });
}

/**
 * Retire a content profile (soft delete).
 *
 * Refused while the profile is the CURRENT content for any device or group.
 * Superseded history is fine — release_deployments stores the profile id with no
 * foreign key, so the row must survive for that history to stay readable.
 */
export function useRetireProfile() {
  const qc = useQueryClient();
  return useMutation({
    // `force` proceeds even when the profile is a device's CURRENT content. That
    // is safe — retiring never touches a device, it only stops the profile being
    // deployed again — so it is a confirmation, not a prohibition. The caller is
    // expected to surface the 409's message before retrying with force.
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      api<{ id: string; name: string; version: string; retired: boolean; forced?: boolean }>(
        `/content/profiles/${encodeURIComponent(id)}${force ? '?force=true' : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'profiles'] });
    },
  });
}

/** Retire an asset (soft delete). Refused while a profile still references it. */
export function useRetireAsset() {
  const qc = useQueryClient();
  return useMutation({
    // `force` proceeds even when a LIVE profile references the asset. The caller
    // must surface the 409's message first — it names the profiles, and the
    // consequence (a device deploying one of them stops receiving the file) is
    // not something to bury behind a generic confirm.
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      api<{ id: string; relpath: string; retired: boolean; forced?: boolean }>(
        `/content/assets/${encodeURIComponent(id)}${force ? '?force=true' : ''}`,
        { method: 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'assets'] });
    },
  });
}

// Move a single raw asset (back-compat; the item move above is preferred).
export function useMoveAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, collection }: { id: string; collection: string }) =>
      api<{ id: string; collection: string; relpath: string }>(
        `/content/assets/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify({ collection }) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['content', 'items'] });
      qc.invalidateQueries({ queryKey: ['content', 'assets'] });
    },
  });
}

// ─── Releases (DE-171, full update-bundle delivery) ──────────────────────
//
// A `release` is composed from registry artifacts (os/app/ucfw — uploaded via
// the scripts/upload-{os,app,fw}.sh CLIs) plus an optional content profile, then
// dispatched as ONE `release` IoT Job to a single device or an AWS IoT
// thing-group. The Portal Releases page is the operator surface for the compose
// + deploy action; the artifact registries themselves are populated out-of-band
// by the upload CLIs (the Portal only reads them). Writes are super-admin-gated
// server-side (RLS + requireRole); the SPA mirrors that.
//
// Meaningful-combination rule (mirrored client-side in the page, enforced 400
// server-side): if an OS part is present the ONLY allowed extra is `content`
// (app + ucfw ship inside the OS image); with no OS, require a non-empty subset
// of {app, content, ucfw}.

export type ArtifactType = 'os' | 'app' | 'fw';

// One registry row. os adds artifact_name + device_type; the rest share the base
// shape. `bytes` is a bigint → string from pg (may also be null). The backend
// returns SELECT * so unknown future columns are tolerated structurally.
export interface ReleaseArtifact {
  id: string;
  version: string;
  artifact_name?: string;        // os only (== version)
  device_type?: string;          // os only — canonical Mender type, see useHardwareTypes (DE-306)
  s3_key: string;
  sha256: string | null;
  bytes: string | number | null;
  notes: string | null;
  created_at: string;
  created_by?: string | null;
  // DE-181 — os only: what the rootfs image SHIPS (bundled QML app + uC fw).
  // Advisory/display-only; null on pre-DE-181 rows (renders as "—").
  embedded_app_version?: string | null;
  embedded_ucfw_version?: string | null;
}

export interface ReleaseArtifactsResponse {
  type: ArtifactType;
  artifacts: ReleaseArtifact[];
}

// The resolved composition recorded in the ledger (parts jsonb). Each sub-object
// is present only when that part was included in the deploy. (Distinct from the
// flat device-jobs `ReleaseParts` above — this is the richer per-artifact detail
// stored in / returned from the release_deployments ledger.)
export interface ReleaseLedgerParts {
  os?: { artifactId: string; version: string; artifactName?: string; deviceType?: string | null };
  /**
   * DE-311 — canonical device type → that board's artifact. The release's
   * hardware COVERAGE is read from this plus the legacy `os` entry; see
   * lib/hardware.ts releaseCoverage.
   */
  osByDeviceType?: Record<string, { artifactId: string; version: string }>;
  content?: { profileId: string; profileName?: string; version: string };
  app?: { artifactId: string; version: string };
  ucfw?: { artifactId: string; version: string };
}

// A CURRENT ledger row — what release is live per target (one row per target).
export interface ReleaseDeployment {
  id?: string;
  target_kind: 'device' | 'thing-group';
  target_id: string;
  release_label: string;
  // DE-220 — public release ID for this deploy; null for historical rows and
  // for deliberately unlabelled internal deploys.
  release_id: string | null;
  job_id: string | null;
  job_status: string | null;
  parts: ReleaseLedgerParts;
  // DE-186 — deployed with force (OS: skip the prompt; non-OS: apply now).
  // Drives the "Staged only" outcome pill (forced intent vs staged outcome).
  force?: boolean;
  created_at: string;
}

export interface ReleaseDeploymentsResponse {
  deployments: ReleaseDeployment[];
}

// POST /releases/deploy request body. Each part references a registry artifact by
// id (os/app/ucfw) or a content profile by id; all parts optional, but the
// combination rule above must hold. `force`: with an OS part it skips the
// on-device install prompt; on a non-OS release (DE-186) it makes the device
// apply the staged parts immediately (idle-gated) instead of waiting for the
// next mains connection.
export interface ReleaseDeployRequest {
  targetKind: 'device' | 'thing-group';
  targetId: string;
  /**
   * The parts to deploy. REQUIRED when publishing a release for the first time.
   * DE-223: omit it together with a `releaseId` to RE-DEPLOY a stored release —
   * the backend then resolves the frozen part set from the releases registry, so
   * the re-deploy is the same bundle by construction.
   */
  parts?: {
    os?: { artifactId: string };
    content?: { profileId: string };
    app?: { artifactId: string };
    ucfw?: { artifactId: string };
  };
  force?: boolean;
  // DE-220/223 — the PUBLIC release ID (YYYY.MM[.N]) this deploy publishes as.
  // Optional: omit it for an internal/dev deploy that should stay unlabelled.
  // Re-using a published ID with a DIFFERENT part set is a 409 (immutability).
  releaseId?: string;
  notes?: string;
}

/**
 * The deploy request's parts with no `undefined` — for compose UI that always has
 * a selection. Distinct from `ReleaseParts` above, which is the job-doc/ledger
 * shape (versions as strings) rather than the request shape (artifact ids).
 */
export type ReleaseDeployParts = NonNullable<ReleaseDeployRequest['parts']>;

export interface ReleaseDeployResponse {
  target: string;
  kind: 'device' | 'thing-group';
  jobId: string;
  // DE-220 — the public release ID this deploy published as, or null when the
  // deploy was intentionally unlabelled. Distinct from releaseLabel below.
  releaseId: string | null;
  // The DERIVED debug label composed from part versions ("os:2.5.1+app:1.21.0").
  // Useful for support; not a customer-facing number.
  releaseLabel: string;
  parts: ReleaseLedgerParts;
  // Capability gate result (reads the RDS-cached gateway.release_capable shadow).
  // For a DEVICE this is definitive (1/1, or a 409 was thrown). For a THING-GROUP
  // it now carries zeros — the deploy path can't resolve membership, so accurate
  // group-scoped buckets come from the pre-deploy useReleaseCapability() preview
  // instead (this field is retained only for the device path / back-compat).
  capability: {
    checked: number; capable: number; unknown: number;
    forceApply?: { capable: number; incapable: number; unknown: number };
  };
}

export function useReleaseArtifacts(type: ArtifactType) {
  return useQuery({
    queryKey: ['releases', 'artifacts', type],
    queryFn: () => api<ReleaseArtifactsResponse>(`/releases/artifacts?type=${type}`),
  });
}

// DE-171 2F — Portal artifact upload. Step 1: presign a direct-to-S3 PUT for the
// given type (the browser PUTs the file straight to S3, bypassing API GW's 10 MB
// cap). Step 2: register the staged object (useRegisterArtifact).
export interface ArtifactUploadUrl {
  uploadUrl: string;
  bucket: string;
  s3Key: string;
}
export function useArtifactUploadUrl() {
  return useMutation({
    mutationFn: (body: { type: ArtifactType; filename: string; contentType?: string; artifactName?: string }) =>
      api<ArtifactUploadUrl>('/releases/artifacts/upload-url', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
  });
}

// Register a staged artifact. os/fw: {type, version, …, s3Key} (backend fills
// sha/bytes); app: {type:'app', zipKey} (backend unpacks the zip + reads its
// app-version.txt). Invalidates that type's registry list on success.
export function useRegisterArtifact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { type: ArtifactType } & Record<string, unknown>) =>
      api<{ type: ArtifactType; artifact: ReleaseArtifact }>('/releases/artifacts', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: ['releases', 'artifacts', vars.type] }),
  });
}

export function useReleaseDeployments() {
  return useQuery({
    queryKey: ['releases', 'deployments'],
    queryFn: () => api<ReleaseDeploymentsResponse>('/releases/deployments'),
    refetchInterval: 30_000,
  });
}

// DE-220/223 — the next free public release ID for the current month
// (YYYY.MM, or YYYY.MM.N when that month already has releases). Server-computed
// from the `releases` registry using the server's UTC clock, so the suggestion
// doesn't drift with the operator's timezone near a month boundary.
export interface NextReleaseIdResponse { releaseId: string; month: string }
export function useNextReleaseId(enabled = true) {
  return useQuery({
    queryKey: ['releases', 'next-id'],
    queryFn: () => api<NextReleaseIdResponse>('/releases/next-id'),
    enabled,
    // Only a suggestion for a form default — no need to poll, but don't serve a
    // stale one after a deploy has consumed it (useDeployRelease invalidates).
    staleTime: 60_000,
  });
}

// DE-220/223 — published release bundles: one row per release ID with the
// frozen part set it names. This is the list of REAL releases, as opposed to the
// per-target derived labels in the deployments ledger.
export interface PublishedRelease {
  release_id: string;
  parts: ReleaseLedgerParts;
  git_commit: string | null;
  build_number: number | null;
  notes: string | null;
  published_at: string;
  published_by: string | null;
}
export interface PublishedReleasesResponse { releases: PublishedRelease[] }
export function usePublishedReleases() {
  return useQuery({
    queryKey: ['releases', 'published'],
    queryFn: () => api<PublishedReleasesResponse>('/releases/published'),
  });
}

// DE-223 — edit a published release's note. The note is the ONLY mutable field:
// the release ID and its frozen part set stay immutable, because devices, release
// notes and the Jira version all key off them.
export function useUpdateReleaseNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ releaseId, notes }: { releaseId: string; notes: string | null }) =>
      api<{ release_id: string; notes: string | null }>(
        `/releases/${encodeURIComponent(releaseId)}`,
        { method: 'PATCH', body: JSON.stringify({ notes }) },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['releases', 'published'] }),
  });
}

// DE-171 2D — live IoT job status overlaid on the deployments ledger. The
// ledger (useReleaseDeployments) is RDS truth from the in-VPC backend, but its
// `job_status` column is never written; the live execution status comes from
// the admin Lambda (outside the VPC, holds iot:DescribeJob*). We POST the
// CURRENT rows' (kind,target,jobId) and merge the returned map by job_id.
export interface ReleaseJobCounts {
  queued: number;
  in_progress: number;
  succeeded: number;
  failed: number;
  rejected: number;
  canceled: number;
  timed_out: number;
  removed: number;
}
export interface ReleaseJobStatus {
  job_id: string;
  kind: 'device' | 'thing-group';
  // QUEUED | IN_PROGRESS | SUCCEEDED | FAILED | TIMED_OUT | REJECTED | CANCELED | REMOVED | UNKNOWN
  status: string;
  queued_at: string | null;
  started_at: string | null;
  last_updated_at: string | null;
  status_details: Record<string, string> | null;
  counts: ReleaseJobCounts | null;
}
export interface ReleaseStatusResponse {
  statuses: Record<string, ReleaseJobStatus>;
}

export function useReleaseStatuses(deployments: ReleaseDeployment[] | undefined) {
  const items = (deployments ?? [])
    .filter((d): d is ReleaseDeployment & { job_id: string } => Boolean(d.job_id))
    .map((d) => ({ kind: d.target_kind, target: d.target_id, jobId: d.job_id }));
  // Stable key from the jobId set → refetch when the deployment set changes.
  const key = items.map((i) => i.jobId).sort().join(',');
  return useQuery({
    queryKey: ['releases', 'status', key],
    enabled: items.length > 0,
    queryFn: () =>
      api<ReleaseStatusResponse>('/admin/release-status', {
        method: 'POST',
        body: JSON.stringify({ items }),
      }),
    refetchInterval: 15_000,
  });
}

// Compose + dispatch ONE release IoT Job to ONE target. The page fans out over
// multiple targets via Promise.allSettled, one atomic RDS+CreateJob txn each
// (mirrors the content deploy). Surfaces backend 400/404/409/502 through ApiError.
// DE-277 — operator-initiated remote reboot of one device's Linux box.
// Super-admin only (backend-enforced). Invalidate the update-jobs card so the
// new reboot job appears immediately rather than after the 15 s poll.
export interface RebootDeviceResponse {
  thing: string;
  jobId: string;
  expiresAt: number;
}

export function useRebootDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { thing: string; force: boolean; reason: string }) =>
      api<RebootDeviceResponse>(`/devices/${encodeURIComponent(vars.thing)}/reboot`, {
        method: 'POST',
        body: JSON.stringify({ force: vars.force, reason: vars.reason }),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['admin', 'device-update-jobs', vars.thing] });
      qc.invalidateQueries({ queryKey: ['device', vars.thing] });
    },
  });
}

export function useDeployRelease() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ReleaseDeployRequest) =>
      api<ReleaseDeployResponse>('/releases/deploy', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['releases', 'deployments'] });
      // DE-223 — a labelled deploy consumes a release ID, so the "next free"
      // suggestion and the published-release list are both stale now.
      if (vars.releaseId) {
        qc.invalidateQueries({ queryKey: ['releases', 'next-id'] });
        qc.invalidateQueries({ queryKey: ['releases', 'published'] });
      }
      // A release that included a content part touches the content ledger /
      // per-device content too; refresh those so the Content surfaces agree.
      if (vars.parts?.content) {
        qc.invalidateQueries({ queryKey: ['content', 'assignments'] });
        qc.invalidateQueries({ queryKey: ['content', 'groups'] });
      }
      qc.invalidateQueries({ queryKey: ['device', vars.targetId] });
      qc.invalidateQueries({ queryKey: ['admin', 'device-update-jobs', vars.targetId] });
    },
  });
}

// DE-171 — pre-deploy, group-SCOPED capability preview. Buckets the SELECTED
// target's devices (applyNow / stageOnly / unsupported / unknown) so the deploy
// UI can warn accurately — replacing the old tenant-wide count (the "+20 devices
// don't support" bug). The in-VPC backend can't resolve thing-group membership,
// so for a group the SPA passes the group's CURRENT members (from useDeviceGroups).
export interface CapabilityBuckets {
  applyNow: string[];
  stageOnly: string[];
  unsupported: string[];
  unknown: string[];
}
export interface CapabilityPreview {
  targetKind: 'device' | 'thing-group';
  targetId: string;
  forcedNonOs: boolean;
  total: number;
  buckets: CapabilityBuckets;
}
export function useReleaseCapability(args: {
  targetKind: 'device' | 'thing-group';
  targetId: string | null;
  members: string[];
  force: boolean;
  hasOs: boolean;
  enabled: boolean;
}) {
  const { targetKind, targetId, members, force, hasOs, enabled } = args;
  const memberKey = [...members].sort().join(',');
  return useQuery({
    queryKey: ['releases', 'capability', targetKind, targetId, force, hasOs, memberKey],
    enabled: enabled && !!targetId,
    queryFn: () =>
      api<CapabilityPreview>('/releases/capability', {
        method: 'POST',
        body: JSON.stringify({ targetKind, targetId, members, force, hasOs }),
      }),
  });
}

// DE-171 — per-device outcomes for ONE group release deploy, fetched LAZILY when
// the operator expands a ledger row (the 15s status poll stays on cheap aggregate
// counts). Reads the admin Lambda (iot:ListJobExecutionsForJob + DescribeJobExecution).
export interface ReleaseJobMember {
  thing: string;
  status: string;
  status_details: Record<string, string> | null;
}
export interface ReleaseJobMembersResponse {
  jobId: string;
  members: ReleaseJobMember[];
  truncated: boolean;
}
export function useReleaseJobMembers(jobId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ['releases', 'job-members', jobId],
    enabled: enabled && !!jobId,
    queryFn: () =>
      api<ReleaseJobMembersResponse>(`/admin/release-job/${encodeURIComponent(jobId!)}/members`),
    refetchInterval: 15_000,
  });
}


// ─────────────────────────────────────────────────────────────────────────
// Portal activity heartbeat (DE-231)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Reports "this person is using the Portal" so the Users page can show a real
 * "Last activity" value instead of a sign-in date. Mounted once, in Layout.
 *
 * Deliberately a useQuery rather than a mutation + setInterval: TanStack Query
 * already pauses `refetchInterval` while the document is hidden (see the note
 * at the top of this file), which is exactly the semantics we want — an open
 * FOREGROUND tab counts as use, a laptop left open overnight does not. Hand
 * rolling that would mean our own timer plus a visibilitychange listener, for
 * no benefit. The POST is idempotent (it stamps "now" for the caller and takes
 * no parameters), so there is nothing lost by driving it from the query path.
 *
 * Failures are swallowed on purpose: a missed heartbeat is a cosmetic gap in an
 * admin column, and must never surface to the person browsing.
 */
export function useActivityHeartbeat() {
  return useQuery({
    queryKey: ['activity-heartbeat'],
    queryFn: async () => {
      try {
        return await api<{ recorded: string }>('/admin/activity', { method: 'POST' });
      } catch {
        return null;
      }
    },
    // 5 min: fine-grained enough that the column reads as live, sparse enough
    // that a day's browsing is ~100 writes across the whole fleet of users.
    refetchInterval: 300_000,
    refetchOnWindowFocus: true,
    retry: false,
    gcTime: 0,
  });
}

// ─────────────────────────────────────────────────────────────────────────
// Onomondo SIM quota (DE-266) — admin Lambda routes
// ─────────────────────────────────────────────────────────────────────────

export interface SimQuota {
  totalBytes: number;
  usedBytes: number;
  remainingBytes: number;
  limitType: string; // minute | hourly | daily | monthly
  period: string | null;
  resetsAt: string | null;
  alertThreshold: number | null;
  blocked: boolean; // hard cap hit — SIM passes no data until resetsAt
}
export interface SimQuotaSummary {
  iccid: string;
  simId: string;
  label: string | null;
  activated: boolean;
  online: boolean;
  lastOnlineAt: string | null;
  quota: SimQuota | null; // null = no data cap set
  fetchedAt: string;
}
// The fleet is mixed-carrier (Telia + Onomondo), so 'not-onomondo' and
// 'sim-not-found' are normal states rendered as labels, never as failures.
export type SimError =
  | 'not-configured'
  | 'not-onomondo'
  | 'sim-not-found'
  | 'vendor-rate-limit'
  | 'vendor-error'
  | 'no-iccid'
  | 'not-visible';
export interface DeviceSimStatus {
  thing: string;
  iccid: string | null;
  sim: SimQuotaSummary | null;
  error: SimError | null;
  message?: string;
}

// 60 s cadence, not the 5 s device poll: this is vendor data behind a 60 s
// Lambda-side cache, so anything faster only rereads that cache.
export function useSimStatus(thing: string | undefined) {
  return useQuery({
    queryKey: ['sim', thing],
    enabled: !!thing,
    queryFn: () => api<DeviceSimStatus>(`/admin/sims/${encodeURIComponent(thing!)}`),
    refetchInterval: 60_000,
    staleTime: 55_000,
    retry: false,
  });
}

export interface SimBatchResponse {
  fetchedAt: string;
  sims: Record<string, DeviceSimStatus>;
}
export function useSimBatch(things: string[], enabled = true) {
  const key = [...things].sort().join(',');
  return useQuery({
    queryKey: ['sim', 'batch', key],
    enabled: enabled && things.length > 0,
    queryFn: () =>
      api<SimBatchResponse>('/admin/sims/batch', {
        method: 'POST',
        body: JSON.stringify({ things }),
      }),
    refetchInterval: 60_000,
    staleTime: 55_000,
    retry: false,
  });
}

// ── DE-279: Greengrass control-plane component health ─────────────────────
// Unlike the shadow's gateway.gg_components (self-published by the telemetry
// GG component, frozen after a reflash wipes the components), this reads what
// the nucleus itself reports to AWS. Same cadence rationale as the SIM hooks:
// the Lambda caches per-thing for 60 s, so faster polling only rereads that.

export type GgVerdict =
  | 'ok'
  | 'drift'
  | 'unhealthy'
  | 'broken'
  | 'missing'
  | 'needs_redeploy'
  | 'no_target'
  | 'no_core'
  | 'error';

export interface GgInstalledComponent {
  name: string;
  version: string;
  state: string | null;
}
export interface GgHealth {
  thing: string;
  verdict: GgVerdict;
  core_status: 'HEALTHY' | 'UNHEALTHY' | null;
  as_of_iso: string | null;
  env_group: EnvGroup | null;
  installed: GgInstalledComponent[];
  target: GroupComponent[] | null;
  missing: string[];
  drift: { name: string; installed: string; target: string }[];
  /** Absent on a pre-remediation Lambda — consumers must tolerate undefined.
   * Execution status on the group's latest deployment job, and what it means
   * for an unhealthy verdict: QUEUED heals itself on next connect; SUCCEEDED
   * (or no execution) never re-sends — that's the reflash case. */
  deployment_exec_status?: string | null;
  remediation?: 'auto_on_connect' | 'in_progress' | 'needs_redeploy' | 'attention' | null;
}

export function useGgHealth(thing: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'gg-health', thing],
    enabled: !!thing,
    queryFn: () => api<GgHealth>(`/admin/devices/${encodeURIComponent(thing!)}/gg-health`),
    refetchInterval: 60_000,
    staleTime: 55_000,
    retry: false, // a 403/missing-route must not hammer; pill renders muted
  });
}

export interface GgHealthBatchResponse {
  fetchedAt: string;
  devices: Record<string, GgHealth>;
}
/** Super-admin only server-side — gate `enabled` on the caller's role. */
export function useGgHealthBatch(things: string[], enabled = true) {
  const key = [...things].sort().join(',');
  return useQuery({
    queryKey: ['admin', 'gg-health', 'batch', key],
    enabled: enabled && things.length > 0,
    queryFn: () =>
      api<GgHealthBatchResponse>('/admin/gg-health/batch', {
        method: 'POST',
        body: JSON.stringify({ things }),
      }),
    refetchInterval: 60_000,
    staleTime: 55_000,
    retry: false,
  });
}

// Raise/clear ONE device's SIM data cap (super-admin). expectedIccid pins the
// mutation to the SIM the operator was looking at — a swap between render and
// click 409s instead of capping the wrong card.
export function useSimTopUp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      thing: string;
      totalBytes: number | null;
      expectedIccid: string;
      reason?: string;
    }) =>
      api<DeviceSimStatus>(`/admin/sims/${encodeURIComponent(v.thing)}/data-limit`, {
        method: 'PUT',
        body: JSON.stringify({
          totalBytes: v.totalBytes,
          expectedIccid: v.expectedIccid,
          reason: v.reason,
        }),
      }),
    // Prefix-invalidate: singles AND batches, so deploy-preview buckets recompute.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sim'] }),
  });
}

export interface SimBulkLimitResponse {
  results: Record<string, { ok: boolean; error?: string; sim?: SimQuotaSummary }>;
}
export function useSimBulkLimit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { things: string[]; totalBytes: number | null; reason?: string }) =>
      api<SimBulkLimitResponse>('/admin/sims/data-limit', {
        method: 'PUT',
        body: JSON.stringify(v),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['sim'] }),
  });
}
